import { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import rocketImage from "../../public/studioErstellen/rocketImage.webp"
import coinImage from "../../public/studioErstellen/coinImage.webp"
import percentageImage from "../../public/studioErstellen/percentageImage.webp"

import { format } from "date-fns";
import { de } from "date-fns/locale";

import 'react-datepicker/dist/react-datepicker.css'
import classes from "./Studio.module.css"

import { useStudio } from "../../hooks/useStudio";
import { useFetchStudioBooking } from "../../hooks/useFetchStudioBooking.js";
import useFetchStudioReviews from "../../hooks/reviews/useFetchStudioReviews.js";
import { useSelector } from "react-redux";
import { useScrollToTop } from "../../util/scrollToTop";
import { useModalSearchParamHistory } from "../../hooks/useModalSearchParamHistory";
import { getStudioStatusMessage, canBookStudio } from "../../util/studioFilters";
import { ensureRedirectPath, storeRedirectPathTo } from "../../util/redirectUtils";
import { parseLocalDateString } from "../../util/parseLocalDate";
import { buildCoarseLocationText } from "../../util/locationPrivacy";
import { useMemo } from "react";

import NavBar from '../../components/NavBar/NavBar.jsx';
import Footer from "../../components/HomePage/Footer";
import StudioInfos from "./StudioInfos.jsx";
import { Booking as BookingProcedure } from "../../components/Booking/Booking"
import StudioGallery from "./StudioGallery/StudioGallery";
import { BookingProvider } from '../../store/bookingStore';
import CreatorNavBar from '../../components/NavBar/CreatorNavBar/CreatorNavBar';
import ProfileMenu from '../../components/NavBar/ProfileMenu';
import ModesSwitch from '../../components/UI/ModesSwitch';
import { SmallModal } from '../../components/UI/SmallModal/SmallModal';
import { isHelpMenuPanelTarget } from '../../util/isHelpMenuPanelTarget';

import StudioMap from './StudioMap';
import Reviews from './Reviews';
import Booking from "./Booking.jsx";
import BookingPhone from "../../components/Booking/BookingPhone/BookingPhone";
import AvailabilityFooter from "./AvailabilityFooter";
import NotFoundError from "../ErrorPages/404Error";
import { NavBarContext } from '../../App';
import { useBooking } from "../../store/bookingStore";
import { useStudioCreatives } from "../../hooks/creative/index";
import { getCloudFrontImageUrl } from "../../util/cloudFrontUtils";
import { useFetchWishlists } from "../../hooks/wishlists/useFetchWishlists";
import { useDeleteWishListStudio } from "../../hooks/wishlists/useDeleteWishListStudio";
import FavouritePhone from "../../components/StudioProfile/FavouriteModal/FavouritePhone";
import {
  trackBookingStarted,
  trackStudioViewed,
} from "../../util/tracking";

export const DatePickerWrapper = ({ children }) => (
  <div className={classes.datePickerWrapper}>{children}</div>
);

export const StudioProfile = ({ isPreviewMode = false, onBackClick }) => {
  const { id } = useParams();
  
  return (
    <BookingProvider studioId={id}>
      <StudioContent isPreviewMode={isPreviewMode} onBackClick={onBackClick} />
    </BookingProvider>
  );
};

const StudioContent = ({ isPreviewMode = false, onBackClick }) => {
  const { bookingData, updateBookingData, clearBookingData } = useBooking();

  const { showSignIn, setShowSignIn, showRegister } = useContext(NavBarContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state;
  const locationStateFrom = locationState?.from;
  const authState = useSelector((state) => state?.auth || {});
  const authUser = authState?.user || {};
  const { profilePicture, firstName, lastName } = authUser;
  const userId = authUser?.id || authUser?._id;
  const hasAuthenticatedUser = Boolean(authState?.isAuthenticated && userId);
  const isAuthChecking = authState?.status === "loading";
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const userContainerRef = useRef(null);
  const viewedStudioIdRef = useRef(null);
  const bookingStartTrackedRef = useRef(null);

  const { studio: data, loading: isLoading, notFound } = useStudio(id);

  // OLD: restrict non-authenticated users to only a small set of studios coming from SearchPage
  // This worked together with `allowedStudioIds` set in SearchPage.jsx.
  // The restriction is now disabled so all studios can be opened.
  // useEffect(() => {
  //   if (!userId && id) {
  //     const allowedStudioIds = sessionStorage.getItem('allowedStudioIds');
  //     if (allowedStudioIds) {
  //       try {
  //         const allowedIds = JSON.parse(allowedStudioIds);
  //         if (Array.isArray(allowedIds) && allowedIds.length > 0) {
  //           const isAllowed = allowedIds.includes(id);
  //           if (!isAllowed) {
  //             navigate('/search', { replace: true });
  //           }
  //         }
  //       } catch (e) {
  //         console.warn('[Studio] Failed to parse allowed studio IDs:', e);
  //       }
  //     }
  //   }
  // }, [id, userId, navigate]);

  const {
    bookingStudioData,
    loading: isBookingLoading,
    error: bookingError,
    availabilityError,
    refetchAvailability,
  } = useFetchStudioBooking(id);

  const { creatives: studioCreativesData, loading: loadingCreatives, error: creativesError } = useStudioCreatives(id);

  const { reviews, loading: loadingReviews, error: reviewsError, loadMoreReviews, hasNextPage } = useFetchStudioReviews({
    studioId: id,
    limit: 20,
  });

  // Check if package-based pricing
  // IMPORTANT:
  // - Only treat the studio as "package pricing" when pricingMode is explicitly "PACKAGES"
  // - Studios with pricingMode === "HOURLY" must stay hourly even if packages exist
  const isPackagePricing = useMemo(
    () => data?.pricingMode === 'PACKAGES',
    [data?.pricingMode]
  );

  const isStudioOwner = useMemo(
    () => Boolean(hasAuthenticatedUser && data?.User?.id && userId === data.User.id),
    [hasAuthenticatedUser, userId, data?.User?.id]
  );

  useEffect(() => {
    if (isPreviewMode || !data?.id || viewedStudioIdRef.current === data.id) return;

    viewedStudioIdRef.current = data.id;
    trackStudioViewed({
      studio: data,
      source: locationStateFrom === 'search' ? 'search_results' : 'studio_profile',
    });
  }, [data, isPreviewMode, locationStateFrom]);

  const directBookingUrlState = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const hasPhoneBookingOpen = params.get("booking-phone") === "open";
    const hasPackageModalOpen = params.get("package-modal") === "open";

    return {
      hasPhoneBookingOpen,
      hasPackageModalOpen,
      hasDirectBookingOpen:
        params.get("booking") === "open" || hasPhoneBookingOpen || hasPackageModalOpen,
    };
  }, [location.search]);

  const availabilityUnavailable = Boolean(bookingStudioData?.busyIntervalsUnavailable);
  const availabilityLoadError = Boolean(bookingError || availabilityError);

  const availabilityUnavailableMessage = userId && availabilityLoadError
    ? "Verfügbarkeit konnte gerade nicht geladen werden. Bitte versuche es erneut."
    : "Melde dich an, um die aktuellen freien Zeiten zu prüfen.";

  // Use a higher-quality image variant for the first-page gallery only.
  const previewGalleryImages = useMemo(() => {
    if (!data?.processedImages?.length) return [];

    const originalKeys = Array.isArray(data?.originalImages) ? data.originalImages : [];

    return data.processedImages.map((fallbackUrl, index) => {
      const imageKey = originalKeys[index];
      if (!imageKey) return fallbackUrl;

      try {
        const isPrimaryImage = index === 0;
        return getCloudFrontImageUrl(imageKey, {
          width: isPrimaryImage ? 1800 : 1200,
          height: isPrimaryImage ? 1350 : 900,
          fit: "cover",
          quality: isPrimaryImage ? 95 : 86,
        });
      } catch (error) {
        return fallbackUrl;
      }
    });
  }, [data?.processedImages, data?.originalImages]);

  // Get lowest package price for footer display
  const lowestPackagePrice = useMemo(() => {
    if (!isPackagePricing || !data?.packages) return null;
    let lowest = Infinity;
    data.packages.forEach(pkg => {
      if (pkg.prices && Array.isArray(pkg.prices)) {
        pkg.prices.forEach(priceEntry => {
          const price = typeof priceEntry.price === 'number' ? priceEntry.price : parseFloat(priceEntry.price || '0');
          if (price > 0 && price < lowest) {
            lowest = price;
          }
        });
      }
    });
    return lowest === Infinity ? null : lowest;
  }, [isPackagePricing, data?.packages]);

  // Get lowest tiered price for footer display
  const lowestTieredPrice = useMemo(() => {
    if (isPackagePricing) return null;
    if (!data?.tieredPricing || !Array.isArray(data.tieredPricing) || data.tieredPricing.length === 0) return null;
    let lowest = Infinity;
    data.tieredPricing.forEach(tier => {
      const price = typeof tier.pricePerHour === 'number' ? tier.pricePerHour : parseFloat(tier.pricePerHour || '0');
      if (price > 0 && price < lowest) {
        lowest = price;
      }
    });
    return lowest === Infinity ? null : lowest;
  }, [isPackagePricing, data?.tieredPricing]);

  const [showNavBar, setShowNavBar] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [studioPrice, setStudioPrice] = useState("");
  const [showCreationSuccess, setShowCreationSuccess] = useState(false);

  const [date, setDate] = useState(() => {
    if (bookingData?.date) {
      return parseLocalDateString(bookingData?.date);
    }
    return null;
  });
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [numberOfPeople, setNumberOfPeople] = useState(1);
  const [packageModalFromOverlay, setPackageModalFromOverlay] = useState(false);
  /** Bumps when the full-screen gallery opens so it remounts (fresh IO + retries) after close/reopen. */
  const [gallerySession, setGallerySession] = useState(0);

  const {
    isOpen: showGallery,
    openOverlay: openGallery,
    closeOverlay: closeGallery,
  } = useModalSearchParamHistory({
    paramName: "gallery",
    paramValue: "open",
    resetKey: id,
  });

  const {
    isOpen: showPackageModal,
    openOverlay: openPackageModal,
    closeOverlay: closePackageModalOverlay,
  } = useModalSearchParamHistory({
    paramName: "package-modal",
    paramValue: "open",
    onInactive: () => setPackageModalFromOverlay(false),
    resetKey: id,
  });

  const setShowPackageModal = useCallback(
    (nextOpen) => {
      if (nextOpen) {
        openPackageModal();
        return;
      }

      closePackageModalOverlay();
    },
    [closePackageModalOverlay, openPackageModal]
  );

  const resetBookingSession = useCallback(({ closePackageModal = true } = {}) => {
    if (closePackageModal) {
      setShowPackageModal(false);
    }
    setPackageModalFromOverlay(false);
    setDate(null);
    setStartTime("");
    setEndTime("");
    setNumberOfPeople("1-5");
    clearBookingData();
  }, [clearBookingData, setShowPackageModal]);

  const {
    isOpen: showBookingProcedure,
    openOverlay: openBookingProcedure,
    closeOverlay: closeBookingProcedureOverlay,
  } = useModalSearchParamHistory({
    paramName: "booking",
    paramValue: "open",
    onInactive: resetBookingSession,
    resetKey: id,
  });

  const setShowBookingProcedure = useCallback(
    (nextOpen) => {
      if (nextOpen) {
        if (!showBookingProcedure) {
          openBookingProcedure();
        }
        return;
      }

      closeBookingProcedureOverlay();
    },
    [closeBookingProcedureOverlay, openBookingProcedure, showBookingProcedure]
  );

  const {
    isOpen: showBookingPhone,
    openOverlay: openBookingPhone,
    closeOverlay: closeBookingPhoneOverlay,
  } = useModalSearchParamHistory({
    paramName: "booking-phone",
    paramValue: "open",
    onInactive: resetBookingSession,
    resetKey: id,
  });

  const [showFavouriteModal, setShowFavouriteModal] = useState(false);

  // Drop legacy favourites query param (equipment overlays use useModalSearchParamHistory in Dropdowns)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const legacyOverlayParams = ["favourites"];
    let changed = false;
    legacyOverlayParams.forEach((key) => {
      if (params.has(key)) {
        params.delete(key);
        changed = true;
      }
    });
    if (changed) {
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "" },
        { replace: true }
      );
    }
  }, [id, location.pathname, location.search, navigate]);

  const [horizontalOffset, setHorizontalOffset] = useState(0);
  // Removed showMessageSender state - now using navigation
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const carouselRef = useRef(null);

  // New state to track when critical images are ready for display
  const [criticalImagesReady, setCriticalImagesReady] = useState(0);
  const [showImageSkeleton, setShowImageSkeleton] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedWishlist, setSelectedWishlist] = useState(null);
  const mobileStudioNameRaw = data?.StudioName?.trim() || bookingStudioData?.StudioName?.trim() || "";
  
  const hasMobileStudioName = Boolean(mobileStudioNameRaw);
  const isStudioNameLoading = isLoading && !hasMobileStudioName;
  const { wishlistsData } = useFetchWishlists(userId);
  const { handleWishlistStudioDeletion } = useDeleteWishListStudio();

  useScrollToTop({ delay: 50 }); // Small delay to handle React Router and loading timing

  // Track when critical images load for skeleton display
  useEffect(() => {
    setCriticalImagesReady(0);

    if (!previewGalleryImages?.length) {
      return;
    }

    const isMobile = window.innerWidth <= 743;
    const requiredImages = isMobile ? 1 : 3;
    const criticalUrls = previewGalleryImages.slice(0, requiredImages).filter(Boolean);

    if (!criticalUrls.length) {
      return;
    }

    let isCancelled = false;
    const loadedUrls = new Set();

    criticalUrls.forEach((imageUrl) => {
      const img = new Image();

      const markLoaded = () => {
        if (isCancelled || loadedUrls.has(imageUrl)) return;
        loadedUrls.add(imageUrl);
        setCriticalImagesReady(loadedUrls.size);
      };

      img.onload = markLoaded;
      img.onerror = markLoaded;
      img.src = imageUrl;

      // Cached image path: if already complete, onload may not fire reliably.
      if (img.complete && img.naturalWidth > 0) {
        markLoaded();
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [previewGalleryImages]);

  useEffect(() => {
    const isMobile = window.innerWidth <= 743;
    const requiredImages = isMobile ? 1 : 3; 
    
    if (criticalImagesReady >= requiredImages) {
      setShowImageSkeleton(false);
    } else {
      setShowImageSkeleton(true);
    }
  }, [previewGalleryImages, criticalImagesReady]);

  const handleImageLoad = (imageType) => {
    setCriticalImagesReady(prev => {
      const newCount = prev + 1;
      return newCount;
    });
  };

  // Drag/Swipe state and handlers for image carousel
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  useEffect(() => {
    if (locationState?.showSuccessModal) {
      setShowCreationSuccess(true);
      const { showSuccessModal, ...restState } = locationState;
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: Object.keys(restState).length ? restState : undefined,
      });
    }
  }, [locationState, location.pathname, location.search, navigate]);

  const handleMouseDown = (e) => {
    if (!data?.processedImages?.length) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !data?.processedImages?.length) return;
    const currentX = e.clientX;
    const diff = currentX - dragStartX;
    const container = carouselRef.current;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (diff / containerWidth) * 100;
    // Hard block edge drag beyond bounds
    let constrained = offsetPercent;
    if (currentImageIndex === 0 && offsetPercent > 0) constrained = 0;
    if (currentImageIndex === data.processedImages.length - 1 && offsetPercent < 0) constrained = 0;
    setDragOffset(constrained);
  };

  const commitDrag = () => {
    if (!isDragging || !data?.processedImages?.length) return;
    const threshold = 5; // percent
    const dragDistance = Math.abs(dragOffset);
    if (dragDistance > threshold) {
      if (dragOffset > 0 && currentImageIndex > 0) {
        setCurrentImageIndex(currentImageIndex - 1);
      } else if (dragOffset < 0 && currentImageIndex < data.processedImages.length - 1) {
        setCurrentImageIndex(currentImageIndex + 1);
      }
    }
    setIsDragging(false);
    setDragOffset(0);
  };

  const handleMouseUp = () => commitDrag();
  const handleMouseLeave = () => { if (isDragging) commitDrag(); };

  const handleCreationSuccessClose = () => {
    setShowCreationSuccess(false);
  };

  const touchStartTimeRef = useRef(0);

  const handleTouchStart = (e) => {
    
    if (!data?.processedImages?.length) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStartX(touch.clientX);
    setDragOffset(0);
    touchStartTimeRef.current = Date.now();
  };

  const handleTouchMove = (e) => {
    
    if (!isDragging) return;
    // Prevent vertical page scroll while swiping carousel
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const diff = currentX - dragStartX;
    const container = carouselRef.current;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (diff / containerWidth) * 100;
    let constrained = offsetPercent;
    if (currentImageIndex === 0 && offsetPercent > 0) constrained = 0; // block right swipe on first image
    if (currentImageIndex === data.processedImages.length - 1 && offsetPercent < 0) constrained = 0; // block left swipe on last image
    setDragOffset(constrained);
  };

  const handleTouchEnd = () => commitDrag();

  const translateX = (() => {
    const base = -100 * currentImageIndex;
    return isDragging ? base + dragOffset : base;
  })();

  // Add this useEffect after the existing useScrollToTop call
  useEffect(() => {
    // iOS Safari navigation fix
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      // Force scroll reset on iOS
      const resetScroll = () => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      };
      
      resetScroll();
      
      // Reset after a short delay to handle any race conditions
      const timeoutId = setTimeout(resetScroll, 100);
      
      // Also reset on next frame
      requestAnimationFrame(resetScroll);
      
      return () => clearTimeout(timeoutId);
    }
  }, [id]);

  useEffect(() => {
    const syncHorizontalOffset = () => {
      setHorizontalOffset(window.scrollX || window.pageXOffset || 0);
    };

    syncHorizontalOffset();
    window.addEventListener("scroll", syncHorizontalOffset, { passive: true });
    window.addEventListener("resize", syncHorizontalOffset);

    return () => {
      window.removeEventListener("scroll", syncHorizontalOffset);
      window.removeEventListener("resize", syncHorizontalOffset);
    };
  }, []);

  const handleGoBackToServicesStudios = () => {
    let fromSearchStorage = false;
    try {
      fromSearchStorage = sessionStorage.getItem('stuStudioBackFrom') === 'search';
    } catch (_) {
      /* ignore */
    }
    const fromSearch = location.state?.from === 'search' || fromSearchStorage;

    const historyState = window.history.state;
    const hasInAppHistory =
      historyState && typeof historyState.idx === 'number' && historyState.idx > 0;

    let decidedAction = 'navigate /services-studios (default)';
    if (isPreviewMode && onBackClick) {
      decidedAction = 'onBackClick (preview; was wrongly hardcoded to services-studios before)';
    } else if (fromSearch) {
      decidedAction = hasInAppHistory
        ? 'navigate(-1) (from search)'
        : 'navigate /search (from search, no stack idx — e.g. new tab)';
    } else if (location.state?.from === 'studio-creation') {
      decidedAction = 'navigate /services-studios replace (studio-creation)';
    } else if (hasInAppHistory) {
      decidedAction = 'navigate(-1) (generic in-app back)';
    }

    const clearSearchExitMarker = () => {
      try {
        sessionStorage.removeItem('stuStudioBackFrom');
      } catch (_) {
        /* ignore */
      }
    };

    if (isPreviewMode) {
      if (location.state?.from === 'studio-creation') {
        navigate('/services-studios', { replace: true });
        return;
      }
      if (location.state?.from === 'editor' && onBackClick) {
        onBackClick();
        return;
      }
      if (onBackClick) {
        onBackClick();
        return;
      }
      navigate('/services-studios', { replace: true });
      return;
    }

    if (fromSearch) {
      clearSearchExitMarker();
      if (hasInAppHistory) {
        navigate(-1);
        return;
      }
      navigate('/search');
      return;
    }

    if (location.state?.from === 'studio-creation') {
      navigate('/services-studios', { replace: true });
      return;
    }

    if (hasInAppHistory) {
      navigate(-1);
      return;
    }

    // Opened directly (no in-app history/context): close should return to home.
    navigate('/', { replace: true });
  };

  const handleGoBack = () => {
    if (isPreviewMode) {
      if (location.state?.from === 'studio-creation') {
        navigate('/services-studios', { replace: true });
        return;
      }
      if (onBackClick) {
        onBackClick();
        return;
      }
      navigate('/services-studios', { replace: true });
      return;
    }

    // Check if user came from studio creation
    if (location.state?.from === 'studio-creation') {
      navigate('/services-studios', { replace: true });
      return;
    }

    // Prefer in-app back navigation when possible; otherwise go to home
    const historyState = window.history.state;
    const hasInAppHistory = historyState && typeof historyState.idx === 'number' && historyState.idx > 0;

    if (hasInAppHistory) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    if (wishlistsData?.items && data?.id) {
      const isInWishlist = wishlistsData.items.some((wishlist) =>
        wishlist.wishListStudios?.items?.some((item) => item?.studioID === data?.id)
      );
      setIsFavorite(isInWishlist);
    }
  }, [wishlistsData, data?.id]);

  useEffect(() => {
    if (selectedWishlist) {
      setIsFavorite(true);
    }
  }, [selectedWishlist]);

  const getAuthRedirectLocation = useCallback((options = {}) => {
    const params = new URLSearchParams(location.search);

    if (options.openBookingPhone) {
      params.set("booking-phone", "open");
    }

    const search = params.toString();

    return {
      pathname: location.pathname,
      search: search ? `?${search}` : "",
      hash: location.hash,
    };
  }, [location.hash, location.pathname, location.search]);

  const navigateToPhoneSignIn = useCallback((options = {}) => {
    const from = getAuthRedirectLocation(options);
    if (options.openBookingPhone) {
      storeRedirectPathTo(from);
    } else {
      ensureRedirectPath(from);
    }

    navigate("/anmelden", {
      state: { background: location, from, requiresAuth: true },
    });
  }, [getAuthRedirectLocation, location, navigate]);

  useEffect(() => {
    if (isPreviewMode || !directBookingUrlState.hasDirectBookingOpen || isAuthChecking) {
      return;
    }

    if (hasAuthenticatedUser) {
      return;
    }

    const from = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };

    storeRedirectPathTo(from);

    if (directBookingUrlState.hasPhoneBookingOpen) {
      navigate("/anmelden", {
        state: { background: location, from, requiresAuth: true },
      });
      return;
    }

    setShowSignIn?.(true);
  }, [
    directBookingUrlState.hasDirectBookingOpen,
    directBookingUrlState.hasPhoneBookingOpen,
    hasAuthenticatedUser,
    isAuthChecking,
    isPreviewMode,
    location,
    navigate,
    setShowSignIn,
  ]);

  const handlePhoneSignInClick = () => {
    navigateToPhoneSignIn();
  };

  const handleFavoriteClickPhone = async () => {
    if (!data?.id) return;

    if (!userId) {
      handlePhoneSignInClick();
      return;
    }

    if (isFavorite) {
      const wishlistStudios =
        wishlistsData?.items?.flatMap(
          (wishlist) =>
            wishlist.wishListStudios?.items?.filter(
              (item) => item?.studioID === data?.id
            ) || []
        ) || [];

      try {
        for (const wishlistStudio of wishlistStudios) {
          await handleWishlistStudioDeletion(wishlistStudio?.id);
        }
        setIsFavorite(false);
        setSelectedWishlist(null);
      } catch (error) {
        console.warn("Error removing from wishlists:", error);
      }
      return;
    }

    setShowFavouriteModal(true);
  };

  const handleImageClick = () => {
    openGallery();
    setGallerySession((n) => n + 1);
  };

  const handleGalleryClose = () => {
    closeGallery();
  };

  const handlePhoneBookingClick = () => {
    if (isStudioOwner) {
      return;
    }
    if (!userId) {
      navigateToPhoneSignIn({ openBookingPhone: true });
      return;
    }
    if (availabilityUnavailable) {
      setShowDatePicker(true);
      return;
    }
    openBookingPhone();
  };

  const handleAvailabilityDateChange = (date) => {
    if (!date) return;

    const formattedDate = format(date, 'yyyy-MM-dd', { locale: de });
    setDate(date);
    updateBookingData('date', formattedDate);

    if (isStudioOwner) {
      setShowDatePicker(false);
      return;
    }

    handlePhoneBookingClick();
    setShowDatePicker(false);
  };

  useEffect(() => {
    const handleResize = () => {
      setShowNavBar(window.innerWidth > 743);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAvailabilityClick = () => {
    if (availabilityUnavailable && !showDatePicker) {
      setShowDatePicker(true);
      return;
    }
    setShowDatePicker(!showDatePicker);
  };

  const handleRetryAvailability = useCallback(() => {
    if (!userId) {
      navigateToPhoneSignIn();
      return;
    }
    refetchAvailability?.();
  }, [navigateToPhoneSignIn, refetchAvailability, userId]);

  const handleCloseDatePicker = () => {
    setShowDatePicker(false);
  };

  useEffect(() => {
    if (data) {
      setStudioPrice(data.pricePerHour);
      
    } else {
      setStudioPrice("");
    }
  }, [data]);

  useEffect(() => {
    if (!isPreviewMode) return;
    
    const handleClickOutside = (event) => {
      if (isHelpMenuPanelTarget(event.target)) {
        return;
      }

      if (
        profileMenuRef.current && 
        !profileMenuRef.current.contains(event.target) &&
        userContainerRef.current && 
        !userContainerRef.current.contains(event.target)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPreviewMode]);

  // Removed message URL parameter handling - now using separate route

  // Removed global message sender exposure - now using navigation

  const closeBookingPhone = () => {
    closeBookingPhoneOverlay();
  };

  const closeBooking = () => {
    const params = new URLSearchParams(location.search);
    const hasNestedPackageModal =
      params.get("booking") === "open" && params.get("package-modal") === "open";

    resetBookingSession({ closePackageModal: !hasNestedPackageModal });

    if (hasNestedPackageModal) {
      params.delete("package-modal");
      params.delete("booking");
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "" },
        { replace: true }
      );
      return;
    }

    closeBookingProcedureOverlay();
  }

  const displayAddress = useMemo(() => {
    if (!data) return '';
    if (data.address) return data.address;
    return buildCoarseLocationText(data);
  }, [data]);

  const isIOSWebKit = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isiOSDevice = /iPad|iPhone|iPod/.test(ua);
    const isWebKit = /WebKit/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    return isiOSDevice && isWebKit;
  }, []);

  const fixedPhoneFooterStyle = useMemo(() => {
    // iOS Safari can "unfix" a fixed element when transforms are applied.
    if (isIOSWebKit) {
      return {
        minWidth: "335px",
        width: "max(100vw, 335px)"
      };
    }

    return {
      transform: `translateX(-${horizontalOffset}px)`,
      minWidth: "335px",
      width: "max(100vw, 335px)"
    };
  }, [horizontalOffset, isIOSWebKit]);

  // Get status message and check if studio can be booked
  const statusMessage = data ? getStudioStatusMessage(data) : null;
  const isBookable = data ? canBookStudio(data) : false;
  const unavailableLabel = statusMessage || 'Aktuell nicht buchbar';
  const canRenderBookingOverlay = hasAuthenticatedUser && !isStudioOwner;
  const shouldShowBookingProcedure = showBookingProcedure && canRenderBookingOverlay;
  const shouldShowBookingPhone = showBookingPhone && canRenderBookingOverlay;
  const shouldShowPackageModal = showPackageModal && canRenderBookingOverlay;

  useEffect(() => {
    const isBookingOpen = shouldShowBookingProcedure || shouldShowBookingPhone;
    if (!data?.id || isPreviewMode || !isBookingOpen) {
      if (!isBookingOpen) {
        bookingStartTrackedRef.current = null;
      }
      return;
    }

    const source = shouldShowBookingPhone ? "booking_phone" : "booking_desktop";
    const signature = `${data.id}:${source}`;
    if (bookingStartTrackedRef.current === signature) return;

    bookingStartTrackedRef.current = signature;
    trackBookingStarted({
      studio: data,
      source,
      value: bookingData?.totalPrice || data?.pricePerHour,
    });
  }, [
    bookingData?.totalPrice,
    data,
    isPreviewMode,
    shouldShowBookingPhone,
    shouldShowBookingProcedure,
  ]);

  if (notFound) {
    return <NotFoundError />;
  }

  return (
      <div
        className={`${classes.container} ${showSignIn || showRegister ? classes.width : ''}`} 
        style={{
          height: shouldShowBookingProcedure || shouldShowBookingPhone || showGallery ? "100vh" : "100%",
          paddingBottom: !showNavBar && !shouldShowBookingPhone ? "85px" : "0",
        }}
      >
        {isPreviewMode ? (
          <>
            <header className={classes.headerStudioProfile}>
              <Link to="/profile" style={{ display: 'none' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="52" height="18" viewBox="0 0 52 18" fill="none">
                  <g clipPath="url(#clip0_8485_11701)">
                    <path d="M9.24209 9.11699L7.89716 9.00528C4.56932 8.73889 3.44854 8.39516 3.44854 7.64756C3.44854 6.89996 4.86244 6.48749 7.77646 6.48749C10.8543 6.48749 12.0958 6.89137 12.3544 7.9741L12.3975 8.14596H15.7771L15.7426 7.90536C15.4064 5.05244 12.8372 3.66895 7.89716 3.66895C2.95712 3.66895 0.422446 5.02666 0.422446 7.69053C0.422446 10.131 2.44846 11.3082 7.4316 11.7551L8.63859 11.8668C11.9406 12.1504 13.1648 12.58 13.1648 13.4393C13.1648 14.376 11.6561 14.8572 8.67308 14.8572C5.29351 14.8572 3.56924 14.2299 3.40543 12.9323L3.37957 12.7433H0V12.9753C0.155184 16.0946 2.98299 17.6757 8.41444 17.6757C13.8459 17.6757 16.1995 16.1977 16.1995 13.2847C16.1995 10.6895 14.2511 9.52086 9.24209 9.11699Z" fill="black"/>
                    <path d="M41.2191 4.09892V9.66726C41.2191 12.7866 39.1586 14.72 35.848 14.72C32.7357 14.72 31.5201 13.3365 31.5201 9.77898V4.09892H21.071V0L17.9587 1.30615V13.0444C17.9587 16.284 19.5278 17.6675 23.2091 17.6675C24.4937 17.6675 25.7266 17.4526 26.7698 17.2378L26.9422 17.2034V14.445L26.6835 14.4966C25.33 14.7716 24.7265 14.8747 23.7609 14.8747C21.9763 14.8747 21.071 14.4536 21.071 12.1421V6.67686H29.951L28.313 7.91427V10.7844C28.313 15.4848 30.3131 17.6675 34.5979 17.6675C37.3654 17.6675 39.6414 16.7136 41.2191 14.8919V17.2378H44.4263V4.09892H41.2191Z" fill="black"/>
                    <path d="M48.9173 7.80391C50.0817 7.80391 51.0257 6.90461 51.0257 5.79526C51.0257 4.68592 50.0817 3.78662 48.9173 3.78662C47.7528 3.78662 46.8088 4.68592 46.8088 5.79526C46.8088 6.90461 47.7528 7.80391 48.9173 7.80391Z" fill="#DF315A"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_8485_11701">
                      <rect width="51.0642" height="17.6761" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </Link>
              <Link to="/">
                <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                  <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                  <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                  <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                </svg>
              </Link>
              <CreatorNavBar onBackClick={handleGoBack} />
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '20px'
              }}>
                <ModesSwitch />
                <div 
                  ref={userContainerRef} 
                  onClick={() => setShowProfileMenu(!showProfileMenu)} 
                  style={{
                    cursor: 'pointer',
                    padding: '2.5px',
                    borderRadius: '129.412px',
                    border: '1px solid var(--dark-grey-2)',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '129.412px',
                    overflow: 'hidden'
                  }}>
                    <img src={profilePicture} alt="User Profile" style={{
                      objectFit: 'cover',
                      width: '40px',
                      height: '40px'
                    }} /> 
                  </div>
                </div>
              </div>
            </header>
            {showProfileMenu && (
              <div ref={profileMenuRef}>
                <ProfileMenu 
                  profilePicture={profilePicture}
                  firstName={firstName}
                  lastName={lastName}
                  setShowProfileMenu={setShowProfileMenu}
                />
              </div>
            )}
          </>
        ) : (
          <div className={classes.phoneHeaderContainer}>
          <div
            onClick={handleGoBackToServicesStudios}
            className={classes.backButtonContainer}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M8.01088 8.49498L0.505859 0.989949L1.49581 0L9.00083 7.50502L16.5059 0L17.4958 0.989949L9.99078 8.49498L17.4958 16L16.5059 16.9899L9.00083 9.48492L1.49581 16.9899L0.505859 16L8.01088 8.49498Z" fill="#1F1F1F"/>
            </svg>
          </div>
            <span className={classes.logoContainer}>
              {isStudioNameLoading ? (
                <span className={classes.studioNameSkeleton} aria-hidden="true"></span>
              ) : (
                <span
                  className={classes.studioNameText}
                  title={data?.name || bookingStudioData?.name || "Studio"}
                >
                  {mobileStudioNameRaw || "Studio"}
                </span>
              )}
            </span>
            <div className={classes.buttonsContainer}>
              <button onClick={handleFavoriteClickPhone} className={classes.likeButton}>
                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="20" viewBox="0 0 23 20" fill="none">
                  <path d="M5.7041 0.624023H5.83496L6.05762 0.635742C7.16824 0.723837 8.27333 1.22049 9.34863 2.16504L9.34961 2.16699C9.69631 2.4695 10.0065 2.80676 10.2949 3.19824L11.3066 4.56934L11.8066 3.87891L12.3047 3.18945L12.3057 3.1875C12.5718 2.81696 12.8875 2.47439 13.2393 2.16699L13.2412 2.16504C14.387 1.15512 15.5668 0.660125 16.7529 0.624023C18.441 0.581884 20.1663 1.48575 21.1445 2.95312C22.0424 4.3001 22.0649 5.81344 21.832 7.04688C21.6149 8.19695 21.1859 9.03737 21.126 9.15234C20.3934 10.4866 19.7203 11.2964 19.2783 11.792C18.1883 13.0059 15.7167 15.4536 11.2969 18.3975C6.56988 15.2558 4.28429 12.9248 3.34375 11.8164C2.71136 11.0689 2.10548 10.2105 1.44141 9.10254L1.42578 9.07715L1.37695 8.97461C1.32921 8.87676 1.26187 8.73178 1.1875 8.54785C1.03826 8.17871 0.861738 7.65901 0.746094 7.05273C0.513821 5.83482 0.537622 4.32491 1.45215 2.95312V2.95215C2.40016 1.52511 4.06959 0.624023 5.7041 0.624023Z"
                    fill={isFavorite ? "var(--stu-rot)" : "white"}
                    stroke={isFavorite ? "var(--stu-rot)" : "black"}
                    strokeWidth="0.75"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
        {showNavBar && !shouldShowBookingProcedure && !isPreviewMode && (
          <NavBar fromSearch={true} />
        )}
        {showImageSkeleton || showGallery || !data || !previewGalleryImages?.length ? (
            <>
              <div className={classes.skeletonImagesContainer}>
              <div className={classes.skeletonImageRight}></div>
              <div className={classes.skeletonContainerLeft}>
                <div className={classes.skeletonSmallImage}></div>
                <div className={classes.skeletonSmallImage}></div>
              </div>
              <div className={`${classes.skeletonContainerLeft} ${classes.displayNone}`}>
                <div className={classes.skeletonSmallImage}></div>
                <div className={classes.skeletonSmallImage}></div>
              </div>
              <div className={classes.skeletonCarousel}></div>
            </div>
            </>
          ) : (
          <div 
            className={`${classes.imagesContainer} ${isPreviewMode ? classes.inPreviewMode : ""}`} 
            style={{ position: 'relative' }}
          >
            {isPreviewMode && (
              <button 
                onClick={handleGoBackToServicesStudios}
                className={classes.previewBackButton}
              >
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="20" viewBox="0 0 11 20" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M0 9.6255L8.99855 0.626953L10.2006 1.82903L2.35773 9.67193L10.2486 18.2204L8.99941 19.3734L0.0172915 9.64279L0 9.6255Z" fill="#1F1F1F"/>
              </svg>
              </button>
            )}
            <div 
              className={classes.imageCarousel} 
              ref={carouselRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{ touchAction: 'pan-x', overflow: 'hidden', cursor: isDragging ? 'grabbing' : 'grab' }}
            >
              <div className={classes.imageCarouselTopContainer}>
                <div className={classes.originalContainer}>
                  <div className={classes.originalIcon}>
                      <img src={rocketImage} alt="rocket" />
                  </div>
                  <span>stu original</span>
                </div>
              </div>
              {data && previewGalleryImages && previewGalleryImages.length > 0 && (
                <>
                  <div 
                    style={{ 
                      display: 'flex', 
                      width: '100%', 
                      height: '100%', 
                      transform: `translateX(${translateX}%)`, 
                      transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                      justifyContent: 'flex-start'

                    }}
                  >
                    {previewGalleryImages.map((imgSrc, index) => (
                      <div key={`slide-${index}`} style={{ minWidth: '100%', height: '100%' }}>
                        <img
                          src={imgSrc}
                          alt={`Studio image ${index + 1}`}
                          className={classes.carouselImage}
                          onClick={() => handleImageClick()}
                          onLoad={() => handleImageLoad('Carousel image')}
                          loading={index === 0 ? 'eager' : 'lazy'}
                          decoding="async"
                          fetchpriority={index === 0 ? 'high' : 'auto'}
                        />
                      </div>
                    ))}
                  </div>
                  <div className={`${classes.imageCounter} slimRegularMiniText`}>
                    {currentImageIndex + 1} / {previewGalleryImages.length}
                  </div>
                </>
              )}
            </div>
            {previewGalleryImages.length > 0 && (
              <div className={classes.imageContainerRight} onClick={() => handleImageClick()}>
                  <img 
                    src={previewGalleryImages[0]} 
                    alt="Studio main image" 
                    onLoad={() => handleImageLoad('Right container image')}
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                  />
              </div>
            )}
            {previewGalleryImages.length > 1 && (
              <div className={classes.firstContainerLeft} onClick={() => handleImageClick()}>
                <div className={classes.smallImagesContainer}>
                    <img 
                      src={previewGalleryImages[1]} 
                      alt="Studio image 2" 
                      onLoad={() => handleImageLoad('First left small image')}
                      loading="eager"
                      decoding="async"
                      fetchpriority="high"
                    />
                </div>
                {previewGalleryImages.length > 2 && (
                  <div className={classes.smallImagesContainer} onClick={() => handleImageClick()}>
                      <img 
                        src={previewGalleryImages[2]} 
                        alt="Studio image 3" 
                        onLoad={() => handleImageLoad('Second left small image')}
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                      />              
                  </div>
                )}
              </div>
            )}
            {previewGalleryImages.length > 3 && (
              <div className={classes.secondContainerLeft} onClick={() => handleImageClick()}>
                <div className={classes.smallImagesContainer}>
                  <img 
                      src={previewGalleryImages[3]} 
                      alt="Studio image 4" 
                      onLoad={() => handleImageLoad('Third left small image')}
                      loading="eager"
                      decoding="async"
                    />
                </div>
                {previewGalleryImages.length > 4 && (
                  <div className={classes.smallImagesContainer} onClick={() => handleImageClick()}>
                    <img 
                        src={previewGalleryImages[4]} 
                        alt="Studio image 5" 
                        onLoad={() => handleImageLoad('Fourth left small image')}
                        loading="eager"
                        decoding="async"
                      />
                  </div>
                )}
              </div>
            )}
          </div>
          )}
          <div className={classes.mainContainer} style={{paddingBottom: isLoading ? "100px" : "0px"}}>
            <div className={classes.infosContainer}>
              {isLoading || loadingReviews || loadingCreatives ? (
                <div className={classes.mainContainer} style={{padding: "0px"}}>
                  <div className={classes.infosContainer}>
                    <div className={classes.skeletonInfosContainer}>
                      <div className={classes.skeletonHeader}>
                        <div className={classes.skeletonTitle}></div>
                        <div className={classes.skeletonSubInfo}></div>
                      </div>
                      <div className={classes.horizontalLine}></div>
                      <div className={classes.skeletonInfoBoxes}>
                        <div className={classes.skeletonInfoBox}></div>
                        <div className={classes.skeletonInfoBox}></div>
                        <div className={classes.skeletonInfoBox}></div>
                      </div>
                      <div className={classes.horizontalLine}></div>
                      <div className={classes.skeletonUserProfile}>
                        <div className={classes.skeletonUserImage}></div>
                        <div className={classes.skeletonUserInfo}></div>
                      </div>
                      <div className={classes.horizontalLine}></div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <StudioInfos 
                    studioCreativesData={studioCreativesData}
                    userId={userId}
                    studio={data}
                    reviews={reviews}
                    isPreviewMode={isPreviewMode}
                    isBookable={isBookable}
                    setShowBookingProcedure={setShowBookingProcedure}
                    setShowBookingPhone={openBookingPhone}
                  />
                </>
              )}
            </div>
            <div className={classes.bookingContainer}>
              {isBookingLoading || isLoading || !data ? (
                <div className={classes.bookingContainer}>
                    <div className={classes.skeletonBookingContainer}>
                      <div className={classes.skeletonBookingHeader}></div>
                      <div className={classes.horizontalLine}></div>
                      <div className={classes.skeletonBookingInput}></div>
                      <div className={classes.horizontalLine}></div>
                      <div className={classes.skeletonBookingButton}></div>
                    </div>
                  </div>
              ) : isBookable ? (
                <Booking
                  studioCreativesData={studioCreativesData}
                  userId={userId}
                  bookingStudioData={bookingStudioData}
                  studioUserId={data?.User?.id}
                  minHours={data?.minNumberOfHours}
                  studioPrice={studioPrice}
                  pricingMode={data?.pricingMode}
                  packages={data?.packages}
                  date={date}
                  setDate={setDate}
                  startTime={startTime}
                  setStartTime={setStartTime}
                  endTime={endTime}
                  setEndTime={setEndTime}
                  numberOfPeople={bookingStudioData?.numberOfPeople}
                  setNumberOfPeople={setNumberOfPeople}
                  showBookingProcedure={shouldShowBookingProcedure}
                  setShowBookingProcedure={setShowBookingProcedure}
                  showPackageModal={shouldShowPackageModal}
                  setShowPackageModal={setShowPackageModal}
                  onClose={closeBookingPhone}
                  isPreviewMode={isPreviewMode}
                />
              ) : (
                <button
                  type="button"
                  className={`${classes.unavailableButton}`}
                  disabled
                  aria-disabled="true"
                > 
                  <span className={`${classes.unavailableButtonInside} meddiumLarge`}>
                  {unavailableLabel}
                  </span>
                </button>
              )}
            </div>
          </div>
          <div className={classes.wrapper}>
              <div style={{marginTop: "20px"}} className={classes.mapCommentsContainer}>
                {/* {!isLoading && (
                  <div className={classes.horizontalLine}></div>
                )} */}
                {isLoading ? (
                  <div className={classes.skeleton}></div>
                ) : data && (data?.mapLatitude && data?.mapLongitude) ? (
                  <StudioMap 
                    marginTop={true} 
                    latitude={data?.mapLatitude}
                    longitude={data?.mapLongitude}
                    address={displayAddress}
                  />
                ) : data && (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p className="regularMiddle" style={{ color: 'var(--dark-grey)' }}>Karte nicht verfügbar</p>
                  </div>
                )}
                {!isLoading && data?.reviews?.nextToken && (
                  <div className={classes.horizontalLine}></div>
                )}
                {!isLoading && !loadingReviews && (
                  <Reviews
                    reviews={reviews}
                  />
                )}
              </div>
          </div>
          {!shouldShowBookingPhone && (
          <div className={classes.footerContainer} style={fixedPhoneFooterStyle}>
            {isBookable && (
            <AvailabilityFooter
              bookingStudioData={bookingStudioData}
              showDatePicker={showDatePicker}
              handleCloseDatePicker={handleCloseDatePicker}
              handleAvailabilityDateChange={handleAvailabilityDateChange}
              availabilityUnavailable={availabilityUnavailable}
              availabilityUnavailableMessage={availabilityUnavailableMessage}
              availabilityUnavailableActionLabel={userId ? "Erneut versuchen" : "Einloggen"}
              onRetryAvailability={handleRetryAvailability}
              isPreviewMode={isPreviewMode}
            />
            )}
            {!(isLoading || isBookingLoading) &&
              <div className={classes.phoneFooterContainer}>
                {isBookable ? (
                <>
                <div className={classes.footerTextContainer}>
                  <div className={classes.footerTextContainer2}>
                    {isLoading || !data ? (
                      <div className={classes.skeleton}></div>
                    ) : (
                      <>
                        {lowestTieredPrice ? (
                          <>
                            <span className="semiboldMiddle">{lowestTieredPrice}€</span>
                            <span className="regularMiddle">ab</span>
                          </>
                        ) : isPackagePricing && lowestPackagePrice ? (
                          <>
                            <span className="semiboldMiddle">{lowestPackagePrice}€</span>
                            <span className="regularMiddle">ab</span>
                          </>
                        ) : (
                          <>
                            <span className="semiboldMiddle">{data.pricePerHour}€</span>
                            <span className="regularMiddle">pro Stunde</span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <span 
                    className={`${classes.availability} ${isPreviewMode ? classes.disabled : ''}`} 
                    onClick={isPreviewMode ? undefined : (isPackagePricing && !isStudioOwner ? handlePhoneBookingClick : handleAvailabilityClick)}
                    disabled={isPreviewMode}
                    style={{ 
                      cursor: isPreviewMode ? 'not-allowed' : 'pointer',
                      opacity: isPreviewMode ? 0.5 : 1
                    }}
                  >
                    Verfügbarkeit zeigen
                  </span>
                </div>
                {isBookable && (
                  <button 
                    style={{
                      display: userId === data?.User?.id ? "none" : "flex",
                      cursor: isPreviewMode ? 'not-allowed' : 'pointer',
                      opacity: isPreviewMode ? 0.5 : 1
                    }}
                    onClick={isPreviewMode ? undefined : handlePhoneBookingClick} 
                    disabled={isPreviewMode}
                    className={`${classes.footerButton} regularLarge`}
                  >
                    Reservieren
                  </button>
                )}
                </>
                ) : (
                  <button
                    type="button"
                    className={`${classes.unavailableButton} ${classes.unavailableButtonMobile} mediumLarge`}
                    disabled
                    aria-disabled="true"
                  >
                    {unavailableLabel}
                  </button>
                )}
              </div>
            }
          </div>
          )}
          <SmallModal
            open={showCreationSuccess}
            onBackdropClick={handleCreationSuccessClose}
            fromDeleteStudio
          >
            <div className={classes.successModalContainer}>
              <button
                onClick={handleCreationSuccessClose}
                className={classes.successModalCloseButton}
                aria-label="Modal schließen"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>
              </button>
              <div className={classes.successModalContent}>
                <header className={classes.successModalHeader}>
                  <button className={classes.rocketButton}>
                    <img src={rocketImage} alt="rocket" />
                    <span>stu original</span>
                  </button>
                  <h3 className="titleSemibold">Glückwunsch! <br/> Du bist ein Stu Original.</h3>
                  <p className="regularMiddle">Als eines der ersten 100 Studios sicherst 
                  du dir damit alle exklusiven Stu Original Vorteile.</p>
                </header>
                <div className={classes.mainSuccessModal}>
                  <div className={classes.successModalItem}>
                    <div className={classes.successModalItemImage}>
                      <img src={rocketImage} alt="Mehr Sichtbarkeit" />
                    </div>
                    <h4 className="semiboldXs">Mehr Sichtbarkeit</h4>
                  </div>
                  <div className={classes.successModalItem}>
                    <div className={classes.successModalItemImage}>
                      <img src={coinImage} alt="Ein exklusives Badge" />
                    </div>
                    <h4 className="semiboldXs">Ein exklusives Badge</h4>
                  </div>
                  <div className={classes.successModalItem}>
                    <div className={classes.successModalItemImage}>
                      <img src={percentageImage} alt="Reduzierte Gebühren" />
                    </div>
                    <h4 className="semiboldXs">Reduzierte Gebühren</h4>
                  </div>
                </div>
              </div>
            </div>
          </SmallModal>
          {showFavouriteModal && (
              <FavouritePhone
                showFavouriteModal={showFavouriteModal}
                onClose={() => setShowFavouriteModal(false)}
                setSelectedWishlist={setSelectedWishlist}
                studioId={data?.id}
                backdropClassName={classes.backdrop}
                backdropVisibleClassName={classes.backdropVisible}
              />
          )}
          {shouldShowBookingProcedure && data && (
            <BookingProcedure 
              studioCreativesData={studioCreativesData}
              processedProfilePicture={data?.processedProfilePicture}
              firstName={data?.firstName}
              studioId={data?.id}
              bookingStudioData={bookingStudioData}
              studio={data}
              setShowBookingProcedure={setShowBookingProcedure}
              onClose={closeBooking}
              userId={userId}
              studioUserId={data?.User?.id}
              minHours={data?.minNumberOfHours}
              studioPrice={studioPrice}
              date={date}
              setDate={setDate}
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
              numberOfPeople={bookingStudioData?.numberOfPeople}
              setNumberOfPeople={setNumberOfPeople}
              showBookingProcedure={shouldShowBookingProcedure}
              showPackageModal={shouldShowPackageModal}
              setShowPackageModal={setShowPackageModal}
              packageModalFromOverlay={packageModalFromOverlay}
              setPackageModalFromOverlay={setPackageModalFromOverlay}
            />
          )}
          {showGallery && data && (
            <StudioGallery 
              key={gallerySession}
              isPreviewMode = {isPreviewMode}
              studioName={data?.studioName}
              images={data?.processedImages}
              imageKeys={data?.originalImages}
              onClose={handleGalleryClose}
            />
          )}
          {shouldShowBookingPhone && data && (
            <BookingPhone 
              studioCreativesData={studioCreativesData}
              data={data}
              bookingStudioData={bookingStudioData}
              onClose={closeBookingPhone}
            />
          )}
          <Footer />
      </div>
  );
};

export default StudioProfile;
